// Always use the global tokenManager, removing the local implementation
const variantSelected = document.getElementById('variant-selected');
const input = document.getElementById('location-input');
const noResultsDiv = document.getElementById('no-results-location');
const popupTrigger = document.getElementById('location-popup-trigger');
let selectedPlace = null;

let autocomplete;
// Variable to store pending location data until apply button is clicked
let pendingLocationData = null;

// Debounce function
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

// Add these variables near the top of the file, after other global declarations
let lastShippingRequest = null;
let shippingRequestCooldown = false;
let shippingCache = {};
const SHIPPING_COOLDOWN_MS = 2000; // 2 seconds cooldown between requests

// Add this variable to store the last inventory data
let lastInventoryData = null;

// Add this state abbreviation mapping after the other global variables
const australianStates = {
  'australian capital territory': 'ACT',
  'new south wales': 'NSW',
  'northern territory': 'NT',
  'queensland': 'QLD',
  'south australia': 'SA',
  'tasmania': 'TAS',
  'victoria': 'VIC',
  'western australia': 'WA'
};

// Helper function to get state abbreviation
function getStateAbbreviation(addressComponents) {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return '';
  }

  // Find the administrative_area_level_1 component (state)
  const stateComponent = addressComponents.find((component) => component.types.includes('administrative_area_level_1'));

  if (!stateComponent) {
    return '';
  }

  const stateName = stateComponent.long_name.toLowerCase();
  return australianStates[stateName] || stateComponent.short_name || '';
}

// New helper function to extract suburb/locality from address components
function getSuburb(addressComponents) {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return '';
  }

  // First try to find locality (suburb)
  const localityComponent = addressComponents.find((component) => component.types.includes('locality'));

  if (localityComponent) {
    return localityComponent.long_name;
  }

  // Fall back to sublocality if available
  const sublocalityComponent = addressComponents.find((component) => component.types.includes('sublocality'));

  return sublocalityComponent ? sublocalityComponent.long_name : '';
}

function initAutocomplete() {
  // Check if Google Maps API is available
  if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
    console.error('Google Maps API is not loaded. Make sure the Google Maps JavaScript API is included with Places library.');

    // Provide fallback functionality without autocomplete
    if (input) {
      // Add basic input handling without Google Maps features
      setupBasicLocationInput();
    }
    return;
  }

  if (!input) {
    console.error('Cannot find location-input element');
    return;
  }
  const suggestions = document.getElementById('suggestions');
  const coordinatesResult = document.getElementById('coordinates-result');
  const submitLocationButton = document.getElementById('close-popup');
  submitLocationButton.addEventListener('click', async () => {
    submitLocationButton.setAttribute('aria-disabled', 'true');
    submitLocationButton.classList.add('loading');
    submitLocationButton.querySelector('.loading__spinner').classList.remove('hidden');

    // Handle location validation and popup closing
    const inputValue = input.value.trim();
    const [lat, lng] = await getLongitudeLatitude({fullAddress: inputValue});

    if (inputValue === '' || !lat || !lng) {
      // Empty input case - show error message
      if (noResultsDiv) {
        noResultsDiv.style.display = 'block';
        this.disableSubmitLocationButton();

        return
      }
    } else {
      // Valid place selected - hide error and close popup
      if (noResultsDiv) {
        noResultsDiv.style.display = 'none';
      }
    }
    // Save pending location data to session storage when apply button is clicked
    if (pendingLocationData) {
      localStorage.setItem('customerLocation', JSON.stringify(pendingLocationData));
      // Update the announcement bar after saving location
      checkCustomerLocation();
      // Clear pending data
      pendingLocationData = null;
    }

    if (lat && lng) {
      // Fetch nearest store based on saved location
      const variantSelected = document.getElementById('variant-selected') || null;
      const ids = variantSelected ? ['gid://shopify/ProductVariant/' + variantSelected.value] : [];

      getNearestStore(lat, lng, ids)
        .then(([nearestStore, inventoryLevels]) => {
          if (nearestStore) {
            // Update inventory and shipping options
            handleFetchInventoryProducts({ inventoryLevels: inventoryLevels });
          } else {
            console.warn('No nearest store found, using default store');
            handleFetchInventoryProducts({ inventoryLevels: inventoryLevels });
          }
          closeLocationPopup();
        })
        .catch((err) => {
          console.error('Error fetching nearest store:', err);
        });
    }
  });
  // Initial options for Google Places Autocomplete
  const initialOptionsComplete = {
    types: ['postal_code', 'locality', 'sublocality'],
    componentRestrictions: { country: 'au' },
    fields: ['place_id', 'name', 'geometry', 'address_components']
  };

  // Check localStorage on page load and pre-fill input
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation) {
    const locationData = JSON.parse(savedLocation);
    input.value = locationData.address;
    coordinatesResult.innerHTML = `Coordinates: Latitude ${locationData.lat}, Longitude ${locationData.lng}`;
  }

  // Initialize Google Places Autocomplete
  autocomplete = new google.maps.places.Autocomplete(input, initialOptionsComplete);

  // Handle place selection
  autocomplete.addListener('place_changed', function () {
    const place = autocomplete.getPlace();
    selectedPlace = place;
    handleAddressSelection(place);
  });

  // Debounced function for fetching suggestions
  const fetchSuggestions = debounce(function (inputValue) {
    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: inputValue,
        ...initialOptionsComplete
      },
      function (predictions, status) {
        suggestions.innerHTML = '';
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          predictions.forEach((prediction) => {
            const div = document.createElement('div');
            div.textContent = prediction.description;
            div.classList.add('suggestion-item');
            div.onclick = () => {
              input.value = prediction.description;
              suggestions.innerHTML = '';
              google.maps.event.trigger(input, 'focus');
              google.maps.event.trigger(input, 'keydown', { keyCode: 13 });
            };
            suggestions.appendChild(div);
          });
        }
      }
    );
  }, 300);

  // Add debounced input listener
  input.addEventListener('input', function () {
    if (input.value.trim()) {
      fetchSuggestions(input.value);
    } else {
      suggestions.innerHTML = '';
    }
    selectedPlace = null;
  });

  // Hide suggestions when clicking outside
  document.addEventListener('click', function (e) {
    if (!input.contains(e.target)) {
      suggestions.innerHTML = '';
    }
  });

  // Handle "Use my current location" button
  document.getElementById('use-current-location').addEventListener('click', function () {
    if (navigator.geolocation) {
      const coordinatesResult = document.getElementById('coordinates-result');
      const input = document.getElementById('location-input');
      const locationWrapper = document.querySelector('.announcement-bar_location-wrapper');

      coordinatesResult.innerHTML = 'Locating...';


      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Reverse geocode to get address and then save location data
          const geocoder = new google.maps.Geocoder();
          const latlng = { lat: lat, lng: lng };
          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results[0]) {
              input.value = results[0].formatted_address;
              // Extract country information
              let country = '';
              let countryCode = '';
              const countryComponent = results[0].address_components.find((component) =>
                component.types.includes('country')
              );
              if (countryComponent) {
                country = countryComponent.long_name;
                countryCode = countryComponent.short_name;
              }

              // Check if the location is in Australia
              if (countryCode !== 'AU') {
                noResultsDiv.style.display = 'block';
                coordinatesResult.innerHTML = `Location detected in ${country}. This service is only available for Australian addresses.`;
                return; // Exit early if not in Australia
              }

              selectedPlace = results[0];
              coordinatesResult.innerHTML = `Coordinates: Latitude ${lat}, Longitude ${lng}`;

              // Extract postal code from address components
              let postalCode = '';
              const postalCodeComponent = results[0].address_components.find((component) =>
                component.types.includes('postal_code')
              );
              if (postalCodeComponent) {
                postalCode = postalCodeComponent.long_name;
              }

              // Get state abbreviation
              const stateAbbr = getStateAbbreviation(results[0].address_components);

              // Get suburb/locality
              const suburb = getSuburb(results[0].address_components);

              // Store location data temporarily until apply button is clicked
              pendingLocationData = {
                address: results[0].formatted_address,
                lat: lat,
                lng: lng,
                postal_code: postalCode,
                state_abbr: stateAbbr,
                suburb: suburb
              };

            } else {
              coordinatesResult.innerHTML = 'Could not find address for this location';
            }
          });
        },
        (error) => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'The request to get location timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred.';
          }
          coordinatesResult.innerHTML = 'Unable to get location: ' + errorMessage;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      coordinatesResult.innerHTML = 'Geolocation is not supported by this browser.';
    }
  });
}

async function getLongitudeLatitude({ address, postcode, fullAddress }) {
  const geocoder = new google.maps.Geocoder();

  const queryAddress = fullAddress || `${address} ${postcode} Australia`;

  if (!queryAddress.trim()) {
    throw new Error("Missing address, postcode, or fullAddress");
  }

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address: queryAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        resolve([lat(), lng()]);
      } else {
        resolve([ null, null ]);
      }
    });
  });
}

function saveLocationToSession(option, name) {
  if (!name) {
    if (option.geometry) {
      // Store location data temporarily until apply button is clicked
      pendingLocationData = {
        address: document.getElementById('location-input').value,
        lat: option.geometry.location.lat(),
        lng: option.geometry.location.lng(),
        postal_code: option.postal_code || ''
      };
    }
  } else {
    const nearestStore = {
      ...option
    };

    localStorage.setItem(name, JSON.stringify(nearestStore));
  }
}

function getNearestStore(lat, lng, ids) {
  return new Promise(async (resolve, reject) => {
    if (!lat || !lng) {
      const error = 'Lat and lng are required!';
      console.error(error);
      reject(error);
      return;
    }

    try {
      const baseUrl = `${window.shopUrl}/apps/service/shopify/nearest-store`;
      const eparcelQuery = isEparcelProduct() ? '&isEparcel=true' : '';
      const query = ids
        ? `variantIds=${ids.join(',')}&lat=${lat}&lng=${lng}${eparcelQuery}`
        : `lat=${lat}&lng=${lng}${eparcelQuery}`;

      const apiUrl = `${baseUrl}?${query}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      if (data?.data?.nearestStore.nearestStore) {
        // Save to session storage
        saveLocationToSession(data.data.nearestStore.nearestStore, 'nearestStore');
        saveLocationToSession(data.data.nearestStore.defaultStore, 'defaultStore');
        resolve([data.data.nearestStore.nearestStore, data.data.inventoryLevels]);
      } else {
        // Use default store for basic functionality instead of rejecting
        if (data?.data?.nearestStore?.defaultStore) {
          saveLocationToSession(data.data.nearestStore.defaultStore, 'nearestStore');
          saveLocationToSession(data.data.nearestStore.defaultStore, 'defaultStore');
          resolve([data.data.nearestStore.defaultStore, data.data.inventoryLevels]);
        } else {
          // Resolve with null instead of rejecting to allow the flow to continue
          console.warn('No store data found, using empty store data');
          resolve(null);
        }
      }
    } catch (err) {
      console.error('Fetch nearest store error:', err);
      // Resolve with null instead of rejecting to allow shipping and pickup to still run
      resolve(null);
    }
  });
}

async function handleFetchInventoryProducts({ inventoryLevels, ids, lat, lng }) {
  let inventoryData;

  if (inventoryLevels) {
    inventoryData = inventoryProducts(inventoryLevels);
  } else if (!ids || !lat || !lng) {
    inventoryData = [];
  } else {
    inventoryData = await fetchInventoryProducts(ids, lat, lng);
  }

  // Store the inventory data for future use
  lastInventoryData = inventoryData;

  // Get shipping data but don't call updateShippingOptions here
  // The debouncedFetchShippingCost call will handle the update
  await handleFetchShippingCost();

  // Only call the debounced version which will use lastInventoryData
  debouncedFetchShippingCost();
  renderStorePickup(lastInventoryData);

  return inventoryData;
}

async function fetchInventoryProducts(ids, lat, lng) {
  try {
    const eparcelQuery = isEparcelProduct() ? '&isEparcel=true' : '';
    const apiUrl = `${window.shopUrl}/apps/service/shopify/inventory-by-ids?variantIds=${ids.join(
      ','
    )}&latitude=${lat}&longitude=${lng}${eparcelQuery}`;

    const response = await fetch(apiUrl, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    let dataProducts = [];
    if (!data?.success) return dataProducts;

    dataProducts = inventoryProducts(data.data);

    return dataProducts;
  } catch (err) {
    console.error('Fetch inventory error:', err);
    return [];
  }
}

function inventoryProducts(inventoryLevels) {
  try {
    let dataProducts = [];

    if (inventoryLevels) {
      const listData = Object.values(inventoryLevels);
      dataProducts = listData
        .map((variant) => {
          const inventoryLevel = variant?.inventoryItem?.inventoryLevel;
          const hasInventoryLevel = inventoryLevel && Object.keys(inventoryLevel).length > 0;
          const variantStock = hasInventoryLevel ? inventoryLevel?.quantities?.[0]?.quantity : 0;
          const maxAvailableQuantity =
            typeof variant.maxAvailableQuantity === 'number' ? variant.maxAvailableQuantity : 0;
          const eparcelReadyToShip =
            typeof variant.eparcelReadyToShip === 'boolean'
              ? variant.eparcelReadyToShip
              : maxAvailableQuantity >= 2;

          if (!hasInventoryLevel && variant.maxAvailableQuantity == null && variant.eparcelReadyToShip == null) {
            return null;
          }

          return {
            variantId: variant.id,
            variantStock: variantStock || 0,
            maxAvailableQuantity,
            eparcelReadyToShip,
            inventoryPolicy: variant.inventoryPolicy,
            tracked: variant.inventoryItem.tracked,
            location: hasInventoryLevel ? inventoryLevel.location : null
          };
        })
        .filter(Boolean);
    }

    return dataProducts;
  } catch (err) {
    console.error('Fetch inventory error:', err);
    return [];
  }
}

// Store the default button text
const DEFAULT_LOCATION_BUTTON_TEXT = 'Set location'; // Change this to match your theme default text

function toggleLocationPopup() {
  const locationPopup = document.getElementById('location-popup');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');

  if (locationPopup.classList.contains('active')) {
    // Popup is currently active, so close it
    locationPopup.classList.remove('active');

    // Restore the button text based on whether location is set
    const savedLocation = localStorage.getItem('customerLocation');
    if (savedLocation) {
      changeButtons.forEach((button) => {
        button.textContent = 'Change';
      });
    } else {
      // When no location is set, reset to default text
      changeButtons.forEach((button) => {
        button.textContent = DEFAULT_LOCATION_BUTTON_TEXT;
      });
    }
  } else {
    // Popup is not active, so open it
    locationPopup.classList.add('active');

    // Change button text to "Close" while popup is open
    changeButtons.forEach((button) => {
      button.textContent = 'Close';
    });
  }
}

function checkCustomerLocation() {
  const locationPopup = document.getElementById('location-popup');
  const savedLocation = localStorage.getItem('customerLocation');
  const locationWrapper = document.querySelector('.announcement-bar_location-wrapper');
  const deliveryWrapper = document.querySelector('.delivery-location');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');

  if (!savedLocation) {
    // No saved location
    // locationPopup.classList.remove('active'); // Ensure popup is hidden initially

    // Reset button classes
    changeButtons.forEach((button) => {
      button.classList.remove('has-location');
      // Preserve original text from theme settings
    });

    // Clear the location wrapper content
    if (locationWrapper) {
      locationWrapper.innerHTML = 'You have not set a location';
    }
    if (deliveryWrapper) {
      deliveryWrapper.innerHTML = 'You have not set a location';
    }
    if (popupTrigger) {
      popupTrigger.textContent = 'Set your location'; // Change this to match your theme default text
    }
  } else {
    // Location exists
    // locationPopup.classList.remove('active'); // Ensure popup is hidden initially

    // Parse the saved location
    const locationData = JSON.parse(savedLocation);

    // Use suburb if available, otherwise fall back to first part of address
    const locality = locationData.suburb || locationData.address.split(',')[0].trim();
    const stateAbbr = locationData.state_abbr || '';

    // Update the location wrapper with the suburb and state abbreviation
    if (locationWrapper) {
      // Create HTML for the location display
      locationWrapper.innerHTML = `
        <span class="announcement-bar_location-selected">
          Your location is ${locality}${stateAbbr ? `, ${stateAbbr}` : ''}
        </span>
      `;
    }

    if (deliveryWrapper) {
      deliveryWrapper.innerHTML = `
        Getting your item at <span class="location-name">${locality}${stateAbbr ? `, ${stateAbbr}` : ''}</span>
      `;
    }

    // Update popup trigger text
    if (popupTrigger) {
      popupTrigger.textContent = 'Change'; // Change this to match your theme default text
    }

    // Update change button to show "Change"
    changeButtons.forEach((button) => {
      button.classList.add('has-location');
      button.textContent = 'Change';
    });
  }
}

function initLocationPopupHandlers() {
  // Use toggle function for the change button
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');
  changeButtons.forEach((button) => {
    button.addEventListener('click', toggleLocationPopup);
  });

  // Add event listener for the delivery location trigger button
  if (popupTrigger) {
    popupTrigger.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toggleLocationPopup();
    });
  }

  // Close popup when overlay is clicked
  const popupOverlay = document.querySelector('.popup-overlay');
  if (popupOverlay) {
    popupOverlay.addEventListener('click', closeLocationPopup);
  }
}

function closeLocationPopup() {
  const locationPopup = document.getElementById('location-popup');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');

  if (locationPopup) {
    locationPopup.classList.remove('active');
    disableSubmitLocationButton();

    // Restore button text based on location status
    const savedLocation = localStorage.getItem('customerLocation');
    if (savedLocation) {
      changeButtons.forEach((button) => {
        button.textContent = 'Change';
      });
    } else {
      // When no location is set, reset to default text
      changeButtons.forEach((button) => {
        button.textContent = DEFAULT_LOCATION_BUTTON_TEXT;
      });
    }
  }
}

function disableSubmitLocationButton() {
  const locationPopup = document.getElementById('location-popup');
  const submitLocationButton = locationPopup.querySelector('#close-popup');

  submitLocationButton.setAttribute('aria-disabled', 'false');
  submitLocationButton.classList.remove('loading');
  submitLocationButton.querySelector('.loading__spinner').classList.add('hidden');
}

// Function to check if Google Maps API is loaded
function waitForGoogleMaps(callback, timeout = 3000) {
  const startTime = Date.now();

  function checkGoogle() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      callback();
    } else if (Date.now() - startTime > timeout) {
      console.error('Google Maps API failed to load within timeout period');
      console.log('Initializing basic location input without Google Maps functionality');
      // Initialize basic functionality without Google Maps
      if (input) {
        setupBasicLocationInput();
      }
    } else {
      setTimeout(checkGoogle, 100);
    }
  }

  checkGoogle();
}

// Add this before initAutocomplete() call
document.addEventListener('DOMContentLoaded', function () {
  // Wait for Google Maps API to be loaded before initializing autocomplete
  waitForGoogleMaps(
    () => {
      initAutocomplete();
    },
    3000 // 3 second timeout
  );

  checkCustomerLocation();
  initLocationPopupHandlers();
  // Check if customer location exists in localStorage
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation && variantSelected) {
    const locationData = JSON.parse(savedLocation);
    const ids = variantSelected ? ['gid://shopify/ProductVariant/' + variantSelected.value] : [];
    // Only call handleFetchInventoryProducts which will handle both inventory and shipping
    handleFetchInventoryProducts({ ids: ids, lat: locationData.lat, lng: locationData.lng });
  }
});

// Replace the handleFetchShippingCost function with this improved version
async function handleFetchShippingCost() {
  const metaProduct = window.meta?.product;

  const userLocation = localStorage.getItem('customerLocation');
  const nearestStore = localStorage.getItem('nearestStore');

  if (!metaProduct || !userLocation) return false;
  // Continue even without nearestStore, but we'll handle that case below

  // Create a cache key based on relevant data
  const cacheKey = `${userLocation}_${nearestStore}_${variantSelected ? variantSelected.value : 'all'}`;

  // Check if we're in cooldown period
  if (shippingRequestCooldown) {
    // console.log('Shipping request rate limited, please wait');
    return lastShippingRequest || false;
  }

  // Check if we have a cached result
  if (shippingCache[cacheKey]) {
    // console.log('Using cached shipping data');
    return shippingCache[cacheKey];
  }

  // Set cooldown flag
  shippingRequestCooldown = true;
  setTimeout(() => {
    shippingRequestCooldown = false;
  }, SHIPPING_COOLDOWN_MS);
  const parsedUserLocation = JSON.parse(userLocation);
  let originData = {};

  if (nearestStore) {
    const parsedNearestStore = JSON.parse(nearestStore);
    if (parsedNearestStore && parsedNearestStore.address) {
      originData = {
        country: 'AU',
        postal_code: parsedNearestStore.address.zip || '0000',
        latitude: parsedNearestStore.address.latitude || parsedUserLocation.lat,
        longitude: parsedNearestStore.address.longitude || parsedUserLocation.lng
      };
    }
  }

  // If no valid store was found, default to user's location
  if (!Object.keys(originData).length) {
    originData = {
      country: 'AU',
      postal_code: parsedUserLocation.postal_code || '0000',
      latitude: parsedUserLocation.lat,
      longitude: parsedUserLocation.lng
    };
  }

  const bodyRequest = {
    destination: {
      country: 'AU',
      postal_code: parsedUserLocation.postal_code,
      latitude: parsedUserLocation.lat,
      longitude: parsedUserLocation.lng
    },
    origin: originData,
    items: metaProduct.variants.map((variant) => ({
      quantity: 1,
      price: variant.price,
      vendor: metaProduct.vendor,
      product_id: metaProduct.id,
      variant_id: variant.id,
      grams: variantSelected.getAttribute('data-weight') || 0
    })),
    currency: 'AUD',
    locale: 'en-AU'
  };

  try {
    const response = await fetch(`${window.shopUrl}/apps/service/shipping/rates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bodyRequest)
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // Cache the result
    shippingCache[cacheKey] = data;
    lastShippingRequest = data;

    return data;
  } catch (err) {
    console.error('Fetch shipping cost error:', err);
    return false;
  }
}

// Create a debounced version of the shipping cost function
const debouncedFetchShippingCost = debounce(async () => {
  try {
    const data = await handleFetchShippingCost();
    const isEparcel = isEparcelProduct();

    if (isEparcel) {
      updateShippingOptions(data, lastInventoryData);
      return;
    }

    if (data) {
      // Pass the lastInventoryData to preserve inventory information
      updateShippingOptions(data, lastInventoryData);
    }
  } catch (err) {
    console.error('Error in debounced shipping cost fetch:', err);
  }
}, 500);

function handleAddressSelection(place) {
  if (!place || !place.geometry) {
    console.error('Place data is incomplete');
    return;
  }
  // console.log(place);

  const lat = place.geometry.location.lat();
  const lng = place.geometry.location.lng();
  const address = document.getElementById('location-input').value;
  const postalCodeComponent = place.address_components?.find((component) => component.types.includes('postal_code'));
  const postalCode = postalCodeComponent ? postalCodeComponent.long_name : '';
  const stateAbbr = getStateAbbreviation(place.address_components);
  const suburb = getSuburb(place.address_components);

  // Store address temporarily until apply button is clicked
  pendingLocationData = {
    address,
    lat,
    lng,
    postal_code: postalCode,
    state_abbr: stateAbbr,
    suburb: suburb
  };
}

function getMetafieldByKey(data, key) {
  try {
    const metafields = data?.location?.metafields?.edges;
    if (!metafields || metafields.length === 0) {
      return null;
    }

    const metafield = metafields.find((edge) => edge.node.key === key);

    if (metafield) {
      try {
        return JSON.parse(metafield.node.value);
      } catch (e) {
        return metafield.node.value;
      }
    }

    return null;
  } catch (error) {
    console.error('Error accessing metafields:', error);
    return null;
  }
}

function isEparcelProduct() {
  return variantSelected?.getAttribute('data-is-eparcel') === 'true';
}

function isEparcelInStock(inventoryData) {
  const item = inventoryData?.[0];
  if (!item) return false;
  if (typeof item.eparcelReadyToShip === 'boolean') return item.eparcelReadyToShip;
  if (typeof item.maxAvailableQuantity === 'number') return item.maxAvailableQuantity >= 2;
  return false;
}

const DEFAULT_EPARCEL_SHIPPING_TEXT =
  '<ul><li>This product will be delivered via Australia Post services.</li><li>An estimate of Shipping Costs will be calculated at the checkout.</li></ul>';

function buildShippingItemHtml(rate, inStock) {
  const tooltipShip = typeof tooltipPriorityShipping != 'undefined' ? tooltipPriorityShipping : null;
  const isEparcel = isEparcelProduct();
  const activeShippingLabel = isEparcel ? window.eparcelShippingLabel : window.shippingLabel;
  // eParcel: always show Australia Post bullets (screenshot 1 + 2). Non-eParcel keeps existing texts.
  const activeShippingText = isEparcel
    ? window.eparcelShippingText || DEFAULT_EPARCEL_SHIPPING_TEXT
    : window.shippingText;
  const priceHtml = rate
    ? `<span class="shipping-price">${rate.currency == 'AUD' ? '$' : ''}${(rate.total_price / 100).toFixed(0)}</span>`
    : '';
  const shippingLabelClass = isEparcel ? 'eparcel' : 'default';
  const tooltipHtml = isEparcel ? '' : renderShippingTooltips(tooltipShip);

  return `
      <span class="shipping-icon">
        <svg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <mask id="mask0_4379_823" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="60" height="60">
          <rect x="0.605957" y="13.0294" width="48" height="48" transform="rotate(-15 0.605957 13.0294)" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_4379_823)">
          <path d="M24.7052 46.9478C23.2316 47.3427 21.8392 47.1616 20.5283 46.4044C19.217 45.6476 18.364 44.5324 17.9691 43.0587L13.3998 44.2831L13.2696 41.2122L18.1885 39.8941C18.4396 39.0383 18.8862 38.2902 19.5283 37.6499C20.1701 37.0093 20.9467 36.5669 21.8582 36.3227C22.7697 36.0784 23.6637 36.0732 24.5401 36.307C25.416 36.5406 26.1768 36.9651 26.8225 37.5807L35.3149 35.3051L34.6768 16.3235L13.5231 21.9916L13.5065 21.4546C13.4616 20.7126 13.6512 20.046 14.0753 19.4548C14.4996 18.8631 15.0795 18.4688 15.8149 18.2717L37.5221 12.4553L37.7879 20.1486L42.6214 18.8535L50.1093 24.7313L50.7362 34.2788L47.6897 35.0951C48.0846 36.5688 47.9036 37.9611 47.1468 39.272C46.3897 40.5833 45.2745 41.4363 43.8011 41.8311C42.3274 42.226 40.9349 42.0449 39.6236 41.2878C38.3128 40.531 37.4599 39.4157 37.065 37.942L28.5943 40.2117C28.9892 41.6854 28.808 43.0777 28.0509 44.3887C27.2941 45.6999 26.1789 46.553 24.7052 46.9478ZM38.1269 30.1517L47.281 27.6989L47.1822 26.2599L41.9489 22.1395L37.8993 23.2246L38.1269 30.1517ZM34.6989 17.5361L34.6768 16.3235L35.3149 35.3051L35.2928 34.0925L34.6989 17.5361ZM9.51721 37.8933L9.4652 34.8014L19.1061 32.2181L19.1581 35.31L9.51721 37.8933ZM11.5314 29.9555L11.4794 26.8636L23.0521 23.7627L23.1042 26.8546L11.5314 29.9555ZM23.9288 44.0501C24.6001 43.8702 25.1074 43.4827 25.4508 42.8875C25.7945 42.2926 25.8764 41.6595 25.6965 40.9882C25.5167 40.3169 25.1292 39.8096 24.534 39.4662C23.9391 39.1225 23.306 39.0405 22.6347 39.2204C21.9634 39.4003 21.456 39.7878 21.1127 40.383C20.769 40.9779 20.687 41.611 20.8669 42.2823C21.0468 42.9536 21.4343 43.4609 22.0294 43.8043C22.6243 44.148 23.2575 44.2299 23.9288 44.0501ZM43.0246 38.9333C43.6956 38.7535 44.203 38.366 44.5466 37.7708C44.8904 37.1759 44.9723 36.5428 44.7924 35.8715C44.6125 35.2002 44.225 34.6928 43.6299 34.3495C43.0347 34.0058 42.4015 33.9239 41.7305 34.1037C41.0592 34.2836 40.5517 34.6711 40.2081 35.2664C39.8644 35.8613 39.7824 36.4944 39.9623 37.1657C40.1422 37.837 40.5297 38.3443 41.1248 38.6877C41.7201 39.0313 42.3533 39.1132 43.0246 38.9333Z" fill="white"/>
          </g>
        </svg>
      </span>
      <div class="shipping-option">
        <div class="shipping-label flex ${shippingLabelClass}">
          <span class="shipping-title">${activeShippingLabel || ''}</span>
          ${tooltipHtml}
        </div>
        ${priceHtml}
      </div>
      ${
        inStock
          ? `<span class="shipping-stock">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="#2BB342"/>
            <path d="M7 13L10 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          In Stock & Ready to Ship
        </span>`
          : ''
      }
      ${inStock ? '<p>Available for priority delivery.</p>' : ''}
      ${activeShippingText ? `<div class="shipping-description ${isEparcel ? 'eparcel' : ''}">${activeShippingText}</div>` : ''}
    `;
}

function updateShippingOptions(shippingData, inventoryData) {
  const shippingOptionsContainer = document.getElementById('shipping-options');

  if (!shippingOptionsContainer) {
    console.warn('Shipping options container not found in the DOM');
    return;
  }

  shippingOptionsContainer.innerHTML = '';

  // Use lastInventoryData as a fallback if inventoryData is not provided
  const dataToUse = inventoryData || lastInventoryData;
  const isEparcel = isEparcelProduct();
  const inStock = isEparcel
    ? isEparcelInStock(dataToUse)
    : dataToUse && dataToUse.length > 0 && dataToUse[0].variantStock > 0;

  if (isEparcel) {
    const rateElement = document.createElement('div');
    rateElement.classList.add('shipping-item');
    rateElement.innerHTML = buildShippingItemHtml(null, inStock);
    shippingOptionsContainer.appendChild(rateElement);
    return;
  }

  if (
    !shippingData ||
    !shippingData.data ||
    !shippingData.data.data ||
    !shippingData.data.data.rates ||
    shippingData.data.data.rates.length === 0
  ) {
    shippingOptionsContainer.innerHTML = `<p style="padding: 0 4px;">No shipping options available</p>`;
    return;
  }

  shippingData.data.data.rates.forEach((rate) => {
    const rateElement = document.createElement('div');
    rateElement.classList.add('shipping-item');
    rateElement.innerHTML = buildShippingItemHtml(rate, inStock);
    shippingOptionsContainer.appendChild(rateElement);
  });
}

function renderShippingTooltips(tooltip) {
  if (tooltip && tooltip.length > 0) {
    return `
    <span class='tooltip svg-wrapper'>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="mask0_898_24595" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
        <rect y="-0.5" width="24" height="24" fill="#D9D9D9"></rect>
        </mask>
        <g mask="url(#mask0_898_24595)">
        <path d="M7.99275 11.4101C8.18308 11.4101 8.34386 11.3445 8.47508 11.2131C8.6063 11.0817 8.67191 10.9208 8.67191 10.7303C8.67191 10.54 8.60619 10.3792 8.47475 10.248C8.3433 10.1169 8.18241 10.0513 7.99208 10.0513C7.80175 10.0513 7.64097 10.117 7.50975 10.2485C7.37853 10.3799 7.31291 10.5408 7.31291 10.7311C7.31291 10.9215 7.37864 11.0823 7.51008 11.2135C7.64153 11.3446 7.80241 11.4101 7.99275 11.4101ZM7.52058 9.02298H8.45908C8.46764 8.69487 8.51569 8.4327 8.60325 8.23648C8.69091 8.04037 8.90353 7.78037 9.24108 7.45648C9.5343 7.16326 9.75886 6.89248 9.91475 6.64415C10.0707 6.39593 10.1487 6.10276 10.1487 5.76465C10.1487 5.19076 9.94258 4.74248 9.53025 4.41981C9.1178 4.09726 8.62997 3.93598 8.06675 3.93598C7.5103 3.93598 7.04986 4.08448 6.68541 4.38148C6.32086 4.67848 6.0608 5.02826 5.90525 5.43081L6.76158 5.77431C6.8428 5.55298 6.98169 5.33737 7.17825 5.12748C7.3748 4.9177 7.66669 4.81281 8.05391 4.81281C8.44791 4.81281 8.73914 4.9207 8.92758 5.13648C9.11614 5.35237 9.21041 5.58976 9.21041 5.84865C9.21041 6.0752 9.14586 6.28248 9.01675 6.47048C8.88775 6.65848 8.72325 6.84009 8.52325 7.01531C8.08558 7.4102 7.80908 7.7252 7.69375 7.96031C7.5783 8.19531 7.52058 8.54954 7.52058 9.02298ZM8.00125 14C7.12525 14 6.30186 13.8338 5.53108 13.5013C4.7603 13.1689 4.08986 12.7177 3.51975 12.1478C2.94964 11.5779 2.49825 10.9078 2.16558 10.1373C1.83303 9.36687 1.66675 8.5437 1.66675 7.66781C1.66675 6.79181 1.83297 5.96842 2.16541 5.19765C2.49786 4.42687 2.94903 3.75642 3.51891 3.18631C4.0888 2.6162 4.75897 2.16481 5.52941 1.83215C6.29986 1.49959 7.12303 1.33331 7.99891 1.33331C8.87491 1.33331 9.6983 1.49953 10.4691 1.83198C11.2399 2.16442 11.9103 2.61559 12.4804 3.18548C13.0505 3.75537 13.5019 4.42554 13.8346 5.19598C14.1671 5.96642 14.3334 6.78959 14.3334 7.66548C14.3334 8.54148 14.1672 9.36487 13.8347 10.1356C13.5023 10.9064 13.0511 11.5769 12.4812 12.147C11.9114 12.7171 11.2412 13.1685 10.4707 13.5011C9.7003 13.8337 8.87714 14 8.00125 14ZM8.00008 13C9.48897 13 10.7501 12.4833 11.7834 11.45C12.8167 10.4166 13.3334 9.15554 13.3334 7.66665C13.3334 6.17776 12.8167 4.91665 11.7834 3.88331C10.7501 2.84998 9.48897 2.33331 8.00008 2.33331C6.51119 2.33331 5.25008 2.84998 4.21675 3.88331C3.18341 4.91665 2.66675 6.17776 2.66675 7.66665C2.66675 9.15554 3.18341 10.4166 4.21675 11.45C5.25008 12.4833 6.51119 13 8.00008 13Z" fill="#2BB342"></path>
        </g>
      </svg>
      <span class="tooltip-message">${ tooltip }</span>
    </span>`;
  }

  return '';
}

function renderStorePickup(inventoryData) {
  const pickUpStoreContainer = document.getElementById('pickup-options');

  if (!pickUpStoreContainer) {
    console.warn('Pickup options container not found in the DOM');
    return;
  }

  const defaultLocation = localStorage.getItem('defaultStore');
  const defaultStoreData = defaultLocation ? JSON.parse(defaultLocation) : null;
  const isEparcel = variantSelected ? variantSelected.getAttribute('data-is-eparcel') : 'false';

  const defaultStoreHtml = document.createElement('div');
  defaultStoreHtml.classList.add('default-store');
  if (defaultLocation && defaultStoreData) {
    defaultStoreHtml.innerHTML = `
    <div class='delivery-item'>
      <span class='delivery-icon'>
        <svg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <mask id="mask0_4379_840" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="60" height="60">
          <rect x="0.605957" y="13.0294" width="48" height="48" transform="rotate(-15 0.605957 13.0294)" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_4379_840)">
          <path d="M11.7184 21.9575L10.9419 19.0597L41.0344 10.9965L41.8108 13.8942L11.7184 21.9575ZM19.0397 48.9835L15.9339 37.3924L13.7794 37.9697L13.0029 35.0719L12.4949 24.8553L42.5873 16.792L47.2556 25.8939L48.0321 28.7917L45.8776 29.369L48.9834 40.9601L46.0856 41.7365L42.9798 30.1454L34.2865 32.4748L37.3923 44.0659L19.0397 48.9835ZM21.161 45.3092L33.7181 41.9446L31.3887 33.2513L18.8317 36.6159L21.161 45.3092ZM15.9596 34.2796L44.2989 26.6861L41.0122 20.3199L15.6229 27.1229L15.9596 34.2796Z" fill="white"/>
          </g>
        </svg>
      </span>
      <div class='delivery-content'>
        <div class='delivery-option'>
          <span class='delivery-title'>Instore & Pick Up</span>
          <span class='delivery-price'>FREE</span>
        </div>
        <div class='delivery-stores'>
          <div class='delivery-store'>
            <div class='store-header'>
              <p class='store-name'>${defaultStoreData?.name}</p>
              <a href='tel:${defaultStoreData?.address?.phone}' class='store-phone delivery-action'>${defaultStoreData?.address?.phone}</a>
            </div>
            <div class='store-details'>
              <p>
                ${defaultStoreData?.address?.address1}<br>
                ${defaultStoreData?.address?.city} ${defaultStoreData?.address?.province} ${defaultStoreData?.address?.zip}, ${defaultStoreData?.address?.country}<br>
              </p>
              ${renderOpeningHoursList(defaultStoreData.openingHours)}
              <p class='store-actions'>
                <a
                  href='https://www.google.com/maps/dir/?api=1&destination=${defaultStoreData?.address?.address1}'
                  target='_blank'
                  class='delivery-action'
                  >Direction</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    `;
  }

  if (!pickUpStoreContainer) {
    console.warn('Pickup options container not found in the DOM');
    return;
  }
  // Always start with empty container
  pickUpStoreContainer.innerHTML = '';

  // Show default store if we don't have specific inventory data
  if (isEparcel === 'true') {
    if (defaultStoreData) {
      pickUpStoreContainer.appendChild(defaultStoreHtml);
    } else {
      pickUpStoreContainer.innerHTML = '';
    }
    return;
  } else {
    // We have inventory data, show it
    if (!inventoryData || inventoryData.length === 0) {
      pickUpStoreContainer.innerHTML = '';
      return;
    } else if (inventoryData && inventoryData.length === 1) {

      inventoryData.forEach((itemStore) => {
        let availablePickup = true;

        if (!itemStore || !itemStore?.location || !itemStore?.location?.localPickupSettingsV2) {
          availablePickup = false;
        } else {
          if (itemStore?.tracked) {
            if (itemStore?.variantStock <= 0 && itemStore?.inventoryPolicy !== 'CONTINUE') {
              availablePickup = false;
            }
          } else {
            if (itemStore?.variantStock < 0) {
              availablePickup = false;
            }
          }
        }

        if (!availablePickup) {
          pickUpStoreContainer.innerHTML = '';
          return;
        }
        const store_link = getMetafieldByKey(itemStore, 'link');
        const openingHours = getMetafieldByKey(itemStore, 'opening_hours');

        const pickUpElement = document.createElement('div');
        pickUpElement.classList.add('delivery-item');

        pickUpElement.innerHTML = `
            <span class='delivery-icon'>
              <svg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <mask id="mask0_4379_840" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="60" height="60">
                <rect x="0.605957" y="13.0294" width="48" height="48" transform="rotate(-15 0.605957 13.0294)" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_4379_840)">
                <path d="M11.7184 21.9575L10.9419 19.0597L41.0344 10.9965L41.8108 13.8942L11.7184 21.9575ZM19.0397 48.9835L15.9339 37.3924L13.7794 37.9697L13.0029 35.0719L12.4949 24.8553L42.5873 16.792L47.2556 25.8939L48.0321 28.7917L45.8776 29.369L48.9834 40.9601L46.0856 41.7365L42.9798 30.1454L34.2865 32.4748L37.3923 44.0659L19.0397 48.9835ZM21.161 45.3092L33.7181 41.9446L31.3887 33.2513L18.8317 36.6159L21.161 45.3092ZM15.9596 34.2796L44.2989 26.6861L41.0122 20.3199L15.6229 27.1229L15.9596 34.2796Z" fill="white"/>
                </g>
              </svg>
            </span>
            <div class='delivery-content'>
              <div class='delivery-option'>
                <span class='delivery-title'>Instore & Pick Up</span>
                <span class='delivery-price'>FREE</span>
              </div>
              <div class='delivery-stores'>
                <div class='delivery-store'>
                  <div class='store-header'>
                    <p class='store-name'>${itemStore?.location?.name}</p>
                    <a href='tel:${itemStore?.location?.address?.phone}' class='store-phone delivery-action'>${
            itemStore?.location?.address?.phone.replace(/\D*(\d{3})\D*(\d{4})\D*(\d{4})\D*/, '$1 $2 $3')
        }</a>
                  </div>
                  <div class='store-details'>
                    <p>
                      ${itemStore?.location?.address?.address1}<br>
                      ${itemStore?.location?.address?.city} ${itemStore?.location?.address?.province} ${
            itemStore?.location?.address?.zip
        }, ${itemStore?.location?.address?.country}<br>
                    </p>
                    ${renderOpeningHoursList(openingHours)}
                    <p class='store-actions'>
                    ${
            store_link
                ? `<a href='${store_link?.url}' target='_blank' class='delivery-action'
                        >View Store Details</a
                      >`
                : ''
        }
                      <a
                        href='https://www.google.com/maps/dir/?api=1&destination=${
            itemStore?.location?.address?.address1
        }'
                        target='_blank'
                        class='delivery-action'
                        >Directions</a
                      >
                    </p>
                  </div>
                </div>
              </div>
            </div>`;

        pickUpStoreContainer.appendChild(pickUpElement);
      });
    }
  }
}

function parseOpeningHoursString(hoursStr) {
  if (!hoursStr || typeof hoursStr !== 'string') return null;

  const dayKeyMap = {
    Sun: 'sunday',
    Mon: 'monday',
    Tue: 'tuesday',
    Wed: 'wednesday',
    Thu: 'thursday',
    Fri: 'friday',
    Sat: 'saturday'
  };

  const result = {};

  hoursStr.split('|').forEach((segment) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    const [dayAbbrev, timeRange] = trimmed.split(',').map((s) => s.trim());
    const key = dayKeyMap[dayAbbrev];
    if (!key) return;

    if (timeRange) {
      result[key] = timeRange;
    }
  });

  return result;
}

function renderOpeningHoursList(openingHours) {
  if (!openingHours) return '';

  let hoursStr = openingHours;
  if (typeof openingHours === 'object' && openingHours !== null && typeof openingHours.value === 'string') {
    hoursStr = openingHours.value;
  }

  if (typeof hoursStr !== 'string' || !hoursStr.trim()) return '';

  const normalized = parseOpeningHoursString(hoursStr);
  if (!normalized || typeof normalized !== 'object') return '';

  const keysByIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const todayIndex = new Date().getDay();
  const todayKey = keysByIndex[todayIndex];
  const todayHours = normalized[todayKey];

  if (!todayHours) return '';

  return `<div class="store-details delivery-store-details__hours-row"><span>Open Today:</span><span>${todayHours}</span></div>`;
}

// Fallback function for basic location input when Google Maps API is not available
function setupBasicLocationInput() {
  if (!input) return;

  const coordinatesResult = document.getElementById('coordinates-result');

  // Handle close popup functionality
  document.getElementById('close-popup').addEventListener('click', () => {
    const inputValue = input.value.trim();

    if (inputValue === '') {
      if (noResultsDiv) {
        noResultsDiv.style.display = 'block';
        return;
      }
    } else {
      if (noResultsDiv) {
        noResultsDiv.style.display = 'none';
      }

      // Save basic location data without coordinates
      const locationData = {
        address: inputValue,
        lat: null,
        lng: null,
        timestamp: Date.now()
      };

      localStorage.setItem('customerLocation', JSON.stringify(locationData));
      closeLocationPopup();
      checkCustomerLocation();
    }
  });

  // Load saved location
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation) {
    const locationData = JSON.parse(savedLocation);
    input.value = locationData.address;
    if (coordinatesResult) {
      coordinatesResult.innerHTML = locationData.lat && locationData.lng
        ? `Coordinates: Latitude ${locationData.lat}, Longitude ${locationData.lng}`
        : 'Coordinates not available (Google Maps API not loaded)';
    }
  }
}
